// CompactActionsRow — iter109.
//
// Single-row, 3-button action layout used on list/card surfaces (My Places,
// My Favorites, and any other compact card that lists a destination with
// lat/lng). Replaces the prominent stacked 2-row RideAndQRButtons layout on
// pages where vertical real-estate matters.
//
// Layout (gap 8px, height 42px each):
//   ┌──────────────────┐ ┌──────────────────┐ ┌────┐
//   │ 🚗 Request Ride  │ │ ▥ View Direct.   │ │ ☎  │ ← only if whatsapp set
//   └──────────────────┘ └──────────────────┘ └────┘
//    gold-filled flex 1    gold-outline flex 1   42px circle outline
//
// The phone circle opens the iter108 ContactPopup (Send Message + Call).
import { useState } from "react";
import { Car, QrCode, Phone } from "lucide-react";
import DriverQRModal from "./DriverQRModal";
import { sendRideRequest, openContactPopup } from "../lib/sendRideRequest";
import { useI18n } from "../lib/i18n";

export default function CompactActionsRow({ destination, testidPrefix }) {
  const { t } = useI18n();
  const [qrOpen, setQrOpen] = useState(false);
  const hasWhatsapp = !!destination.whatsapp;

  return (
    <div
      className="mt-3"
      style={{ display: "flex", gap: 8, alignItems: "center" }}
      data-testid={`${testidPrefix}-buttons`}
    >
      <button
        type="button"
        data-testid={`${testidPrefix}-request-driver`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          sendRideRequest(destination, { source: testidPrefix });
        }}
        className="inline-flex items-center justify-center gap-1.5 transition-opacity hover:opacity-95"
        style={{
          flex: 1,
          height: 42,
          background: "linear-gradient(135deg, #d4b25a 0%, #E8B84B 100%)",
          color: "#0B0B0B",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: "0.02em",
          borderRadius: 10,
          border: "none",
          padding: "0 10px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <Car className="w-4 h-4 shrink-0" strokeWidth={2} />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{t("ride_request_driver")}</span>
      </button>

      <button
        type="button"
        data-testid={`${testidPrefix}-get-qr`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQrOpen(true); }}
        className="inline-flex items-center justify-center gap-1.5 transition-colors hover:bg-white/[0.04]"
        style={{
          flex: 1,
          height: 42,
          background: "transparent",
          color: "#E8B84B",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: "0.02em",
          borderRadius: 10,
          border: "1.5px solid rgba(232,184,75,0.55)",
          padding: "0 10px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <QrCode className="w-4 h-4 shrink-0" strokeWidth={2} />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{t("ride_view_directions")}</span>
      </button>

      {hasWhatsapp && (
        <button
          type="button"
          data-testid={`${testidPrefix}-phone`}
          aria-label={t("ride_phone_contact")}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openContactPopup(destination, { source: `${testidPrefix}-phone` });
          }}
          className="grid place-items-center transition-colors hover:bg-white/[0.04]"
          style={{
            width: 42,
            height: 42,
            flex: "0 0 42px",
            borderRadius: 999,
            background: "transparent",
            color: "#E8B84B",
            border: "1.5px solid rgba(232,184,75,0.55)",
            padding: 0,
          }}
        >
          <Phone className="w-4 h-4" strokeWidth={2} />
        </button>
      )}

      <DriverQRModal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        business={destination}
        testidPrefix={`${testidPrefix}-qr`}
      />
    </div>
  );
}
